import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnChanges, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { ElementHeightService } from '@df/dom/element-height/element-height.service';
import { TimeoutService } from '@df/ng/timeout.service';
import { ScrollService } from '@df/util/service/scroll.service';
import { Subscription } from 'rxjs';
import { StickyFreeService } from './sticky-free.service';

let uid = 0;

@Component({
    selector: 'sticky-free',
    templateUrl: './sticky-free.component.html',
    styleUrl: './sticky-free.component.scss',
    standalone: true,
    imports: [ElementHeightDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StickyFreeComponent implements OnInit, OnDestroy, OnChanges {
    private readonly _uid = `stickyfree${uid++}`;

    @Input()
    order?: number;

    @Input()
    stickyOff?: boolean;

    @Input()
    stickyId?: string;

    @Input()
    stickyOffset: number | string = 0;

    @Input()
    isStickyDownOffset: number = 0;

    get uid() {
        return this.stickyId || this._uid;
    }

    private isSticky = false;
    protected isStickyOn = false;
    private initiated = false;

    private unregister?: () => void;
    private subscription?: Subscription;
    private initialSubscription?: Subscription;
    protected subscriptions = new Subscription();
    protected scrollSubscription?: Subscription;
    private scrollService = inject(ScrollService);
    private renderer2 = inject(Renderer2);
    private elementRef = inject(ElementRef);
    private stickyFreeService = inject(StickyFreeService);
    private elementHeightService = inject(ElementHeightService);
    protected app = inject(App);
    private timeoutService = inject(TimeoutService);
    private meta = inject(Meta);

    ngOnInit() {
        if (this.app.isPlatformBrowser) {
            this.applySticky();
            this.initialSubscription = this.elementHeightService.getState(this.uid).subscribe(value => {
                if (value !== undefined) {
                    this.renderer2.setStyle(this.elementRef.nativeElement, 'height', this.stickyFreeService.toVar(this.uid));
                    this.renderer2.addClass(this.elementRef.nativeElement, 'is-ready');

                    this.timeoutService.setTimeout(() => {
                        this.initialSubscription?.unsubscribe();
                        this.initialSubscription = undefined;
                    });
                }
            });

            this.scrollSubscription = this.scrollService.scroll$.subscribe(
                this.timeoutService.throttle(
                    () => {
                        if (this.isStickyOn) {
                            const top = this.elementRef.nativeElement.getBoundingClientRect().top;
                            const stickyOffset =
                                typeof this.stickyOffset === 'number'
                                    ? this.stickyOffset
                                    : this.elementHeightService.getState(this.stickyOffset).value || 0;

                            this.updateIsStickyClass(top * -1 > stickyOffset);
                        }
                    },
                    500,
                    { leading: true, trailing: true }
                )
            );

            this.subscriptions.add(this.scrollService.isScrollDown$.subscribe(value => this.scrollDown(value)));
            this.subscriptions.add(this.meta.routerEvents.subscribe(routerEvent => {
                if (routerEvent instanceof NavigationEnd) {
                    // when we navigate to a new page, we need to reset the sticky state
                    this.scrollDown(false);
                    this.updateIsStickyClass(false);
                }
            }));

            this.initiated = true;
        }
    }

    ngOnChanges(changes) {
        if (this.initiated) {
            if (changes.stickyOff) {
                this.applySticky();
            }
        }
    }

    ngOnDestroy() {
        this.unsetSticky();
        this.initialSubscription?.unsubscribe();
        this.scrollSubscription?.unsubscribe();
    }

    private applySticky() {
        this.stickyOff ? this.unsetSticky() : this.setSticky();
    }

    private setSticky() {
        if (this.isStickyOn) {
            return;
        }
        this.isStickyOn = true;
        this.renderer2.removeClass(this.elementRef.nativeElement, 'isOff');
        if (this.order !== undefined) {
            this.subscription = this.stickyFreeService.entriesChange.subscribe(() => {
                    this.elementRef.nativeElement.style.setProperty('--sticky-offset', this.stickyFreeService.getOffsets(this.order || 0));
            });
            this.unregister = this.stickyFreeService.add({
                uid: this.uid,
                order: this.order
            });
        }
    }

    private unsetSticky() {
        if (!this.isStickyOn) {
            return;
        }
        this.isStickyOn = false;
        this.renderer2.addClass(this.elementRef.nativeElement, 'isOff');
        if (this.unregister) {
            this.unregister();
            this.unregister = undefined;
        }
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }
    }

    protected updateIsStickyClass(isSticky = this.isSticky) {
        if (!this.isStickyOn) {
            return;
        }

        if (this.isSticky !== isSticky) {
            isSticky
                ? this.renderer2.addClass(this.elementRef.nativeElement, 'is-sticky')
                : this.renderer2.removeClass(this.elementRef.nativeElement, 'is-sticky');

            this.isSticky = isSticky;
        }
    }

    protected scrollDown(value: boolean) {
        if (!this.isStickyOn) {
            return;
        }

        const top = this.elementRef.nativeElement.getBoundingClientRect().top;
        const isPastDownOffset = this.isStickyDownOffset && top * -1 > this.isStickyDownOffset;

        if (!this.isStickyDownOffset || isPastDownOffset) {
            value
                ? this.renderer2.addClass(this.elementRef.nativeElement, 'is-sticky-down')
                : this.renderer2.removeClass(this.elementRef.nativeElement, 'is-sticky-down');
        }
    }
}
